import { useState, useEffect } from 'react'
import './ITAdminInfoEditor.css'

const API_BASE_URL = window.location.origin + '/api'

// Типы блоков
type BlockType = 'text' | 'list' | 'link' | 'faq' | 'wifi' | 'contact'

interface SubBlock {
  id: string
  type: BlockType
  title?: string
  content: any
}

interface Block {
  id: string
  type: BlockType
  title: string
  content: any
  subBlocks?: SubBlock[]
  order: number
}

interface ITAdminContent {
  blocks: Block[]
}

const generateId = () => Math.random().toString(36).substring(2, 15)

const DEFAULT_CONTENT: ITAdminContent = {
  blocks: [
    {
      id: generateId(),
      type: 'text',
      title: '📢 Důležité oznámení',
      content: 'Vítejte v intranetovém systému školy. Tento systém slouží pro efektivní komunikaci a správu interních procesů školy.',
      order: 0
    },
    {
      id: generateId(),
      type: 'contact',
      title: '💻 Technická podpora',
      content: {
        description: 'Pro technickou podporu, hlášení problémů nebo dotazy ohledně systému kontaktujte IT oddělení školy.',
        email: 'it@iss-cheb.cz',
        phone: '+420 XXX XXX XXX'
      },
      order: 1
    },
    {
      id: generateId(),
      type: 'wifi',
      title: '📶 Seznam školních Wi-Fi sítí',
      content: [
        { name: 'Škola na Májové', ssid: 'ISS-M68', password: '' },
        { name: 'Škola na Hrázi', ssid: 'ISS-H1', password: '' },
        { name: 'Škola na Komenského', ssid: 'ISS-K29', password: '' },
        { name: 'Budova administrativy', ssid: 'ISS-HOST', password: '' }
      ],
      order: 2
    },
    {
      id: generateId(),
      type: 'link',
      title: '📧 Školní licence Microsoft Office 365',
      content: {
        description: 'Škola má licenci Microsoft Office 365, kterou mohou studenti a učitelé využívat online.',
        link: 'https://www.office.com/'
      },
      order: 3
    },
    {
      id: generateId(),
      type: 'link',
      title: '🤖 Školní licence Sciobot',
      content: {
        description: 'Naše škola má také licenci pro aplikaci Sciobot, která umožňuje využívat nástroje umělé inteligence pro vzdělávání a správu výuky.',
        link: 'https://app.sciobot.cz/invite-staff?c=ZU84HEA'
      },
      order: 4
    },
    {
      id: generateId(),
      type: 'list',
      title: '🔐 Bezpečnostní tipy',
      content: [
        'Nikdy nesdílejte své přihlašovací údaje s ostatními',
        'Používejte silná hesla a pravidelně je měňte',
        'Při podezření na bezpečnostní problém okamžitě kontaktujte IT oddělení',
        'Při odchodu z počítače se vždy odhlaste'
      ],
      order: 5
    },
    {
      id: generateId(),
      type: 'faq',
      title: '❓ Časté dotazy',
      content: [
        {
          question: 'Jak mohu nahlásit závadu?',
          answer: 'Použijte systém žádek v sekci "Hlášení závad". Vyplňte formulář s popisem problému a systém automaticky předá žádost příslušnému správci.'
        },
        {
          question: 'Jak mohu požádat o nákup?',
          answer: 'V sekci „Žádanky o nákup“ otevřete kartu Žadatel, vytvořte žádost a vyberte svého vedoucího. Po jeho schválení pokračuje žádost k ekonomovi a řediteli.'
        },
        {
          question: 'Zapomněl jsem heslo. Co mám dělat?',
          answer: 'Kontaktujte IT oddělení školy. Heslo můžete resetovat pomocí svého školního emailu.'
        }
      ],
      order: 6
    }
  ]
}

export default function ITAdminInfoEditor() {
  const [content, setContent] = useState<ITAdminContent>(DEFAULT_CONTENT)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    loadContent()
  }, [])

  const loadContent = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/it-admin-info`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      })

      if (response.ok) {
        const data = await response.json()
        if (data.content && data.content.blocks && Array.isArray(data.content.blocks)) {
          setContent(data.content)
        } else {
          setContent(DEFAULT_CONTENT)
        }
      }
    } catch (error) {
      console.error('Error loading content:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const response = await fetch(`${API_BASE_URL}/it-admin-info`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content })
      })

      if (response.ok) {
        alert('Obsah byl úspěšně uložen!')
      } else {
        const error = await response.json()
        alert('Chyba při ukládání: ' + (error.error || 'Neznámá chyba'))
      }
    } catch (error) {
      console.error('Error saving content:', error)
      alert('Chyba při ukládání obsahu')
    } finally {
      setSaving(false)
    }
  }

  const addBlock = (type: BlockType) => {
    const newBlock: Block = {
      id: generateId(),
      type,
      title: 'Nový blok',
      content: getDefaultContentForType(type),
      order: content.blocks.length
    }
    setContent({
      ...content,
      blocks: [...content.blocks, newBlock]
    })
  }

  const deleteBlock = (blockId: string) => {
    if (!confirm('Opravdu chcete smazat tento blok?')) return
    setContent({
      ...content,
      blocks: content.blocks.filter(b => b.id !== blockId)
    })
  }

  const addSubBlock = (blockId: string, type: BlockType) => {
    const newSubBlock: SubBlock = {
      id: generateId(),
      type,
      title: 'Nový podblok',
      content: getDefaultContentForType(type)
    }
    setContent({
      ...content,
      blocks: content.blocks.map(block =>
        block.id === blockId
          ? { ...block, subBlocks: [...(block.subBlocks || []), newSubBlock] }
          : block
      )
    })
  }

  const deleteSubBlock = (blockId: string, subBlockId: string) => {
    setContent({
      ...content,
      blocks: content.blocks.map(block =>
        block.id === blockId
          ? { ...block, subBlocks: (block.subBlocks || []).filter(sb => sb.id !== subBlockId) }
          : block
      )
    })
  }

  const moveBlock = (blockId: string, direction: 'up' | 'down') => {
    const blocks = [...content.blocks]
    const index = blocks.findIndex(b => b.id === blockId)
    if (index === -1) return

    const newIndex = direction === 'up' ? index - 1 : index + 1
    if (newIndex < 0 || newIndex >= blocks.length) return

    ;[blocks[index], blocks[newIndex]] = [blocks[newIndex], blocks[index]]
    blocks[index].order = index
    blocks[newIndex].order = newIndex

    setContent({ ...content, blocks })
  }

  const getDefaultContentForType = (type: BlockType): any => {
    switch (type) {
      case 'text':
        return ''
      case 'list':
        return ['']
      case 'link':
        return { description: '', link: '' }
      case 'faq':
        return [{ question: '', answer: '' }]
      case 'wifi':
        return [{ name: '', ssid: '', password: '' }]
      case 'contact':
        return { description: '', email: '', phone: '' }
      default:
        return ''
    }
  }

  const updateBlock = (blockId: string, field: string, value: any) => {
    setContent({
      ...content,
      blocks: content.blocks.map(block =>
        block.id === blockId ? { ...block, [field]: value } : block
      )
    })
  }

  const updateBlockContent = (blockId: string, value: any) => {
    setContent({
      ...content,
      blocks: content.blocks.map(block =>
        block.id === blockId ? { ...block, content: value } : block
      )
    })
  }

  const updateSubBlock = (blockId: string, subBlockId: string, field: string, value: any) => {
    setContent({
      ...content,
      blocks: content.blocks.map(block =>
        block.id === blockId
          ? {
              ...block,
              subBlocks: (block.subBlocks || []).map(sb =>
                sb.id === subBlockId ? { ...sb, [field]: value } : sb
              )
            }
          : block
      )
    })
  }

  const renderBlockEditor = (block: Block) => {
    return (
      <div key={block.id} className="block-editor">
        <div className="block-header">
          <div className="block-controls">
            <button
              onClick={() => moveBlock(block.id, 'up')}
              disabled={block.order === 0}
              className="move-button"
              title="Přesunout nahoru"
            >
              ⬆️
            </button>
            <button
              onClick={() => moveBlock(block.id, 'down')}
              disabled={block.order === content.blocks.length - 1}
              className="move-button"
              title="Přesunout dolů"
            >
              ⬇️
            </button>
            <button
              onClick={() => addSubBlock(block.id, 'text')}
              className="add-subblock-button"
              title="Přidat podblok"
            >
              ➕ Podblok
            </button>
            <button
              onClick={() => deleteBlock(block.id)}
              className="delete-button"
              title="Smazat blok"
            >
              🗑️
            </button>
          </div>
        </div>

        <div className="editor-section">
          <label>Typ bloku:</label>
          <select
            value={block.type}
            onChange={(e) => {
              const newType = e.target.value as BlockType
              updateBlock(block.id, 'type', newType)
              updateBlockContent(block.id, getDefaultContentForType(newType))
            }}
            className="editor-select"
          >
            <option value="text">📝 Text</option>
            <option value="list">📋 Seznam</option>
            <option value="link">🔗 Odkaz</option>
            <option value="faq">❓ FAQ</option>
            <option value="wifi">📶 Wi-Fi</option>
            <option value="contact">💻 Kontakt</option>
          </select>

          <label>Nadpis bloku:</label>
          <input
            type="text"
            value={block.title}
            onChange={(e) => updateBlock(block.id, 'title', e.target.value)}
            className="editor-input"
            placeholder="Nadpis bloku"
          />

          {/* Редактор содержимого в зависимости от типа */}
          {block.type === 'text' && (
            <textarea
              value={block.content || ''}
              onChange={(e) => updateBlockContent(block.id, e.target.value)}
              className="editor-textarea"
              rows={4}
              placeholder="Text obsahu"
            />
          )}

          {block.type === 'list' && (
            <div>
              {Array.isArray(block.content) ? (
                block.content.map((item: string, index: number) => (
                  <div key={index} className="list-item">
                    <input
                      type="text"
                      value={item}
                      onChange={(e) => {
                        const newList = [...block.content]
                        newList[index] = e.target.value
                        updateBlockContent(block.id, newList)
                      }}
                      className="editor-input"
                      placeholder={`Položka ${index + 1}`}
                    />
                    <button
                      onClick={() => {
                        const newList = block.content.filter((_: any, i: number) => i !== index)
                        updateBlockContent(block.id, newList)
                      }}
                      className="remove-item-button"
                    >
                      🗑️
                    </button>
                  </div>
                ))
              ) : null}
              <button
                onClick={() => {
                  const newList = [...(block.content || []), '']
                  updateBlockContent(block.id, newList)
                }}
                className="add-item-button"
              >
                ➕ Přidat položku
              </button>
            </div>
          )}

          {block.type === 'link' && (
            <div>
              <label>Popis:</label>
              <textarea
                value={block.content?.description || ''}
                onChange={(e) =>
                  updateBlockContent(block.id, {
                    ...block.content,
                    description: e.target.value
                  })
                }
                className="editor-textarea"
                rows={2}
              />
              <label>Odkaz:</label>
              <input
                type="url"
                value={block.content?.link || ''}
                onChange={(e) =>
                  updateBlockContent(block.id, {
                    ...block.content,
                    link: e.target.value
                  })
                }
                className="editor-input"
              />
            </div>
          )}

          {block.type === 'faq' && (
            <div>
              {Array.isArray(block.content) ? (
                block.content.map((item: any, index: number) => (
                  <div key={index} className="faq-item">
                    <label>Otázka:</label>
                    <input
                      type="text"
                      value={item.question || ''}
                      onChange={(e) => {
                        const newFaq = [...block.content]
                        newFaq[index] = { ...newFaq[index], question: e.target.value }
                        updateBlockContent(block.id, newFaq)
                      }}
                      className="editor-input"
                    />
                    <label>Odpověď:</label>
                    <textarea
                      value={item.answer || ''}
                      onChange={(e) => {
                        const newFaq = [...block.content]
                        newFaq[index] = { ...newFaq[index], answer: e.target.value }
                        updateBlockContent(block.id, newFaq)
                      }}
                      className="editor-textarea"
                      rows={2}
                    />
                    <button
                      onClick={() => {
                        const newFaq = block.content.filter((_: any, i: number) => i !== index)
                        updateBlockContent(block.id, newFaq)
                      }}
                      className="remove-item-button"
                    >
                      🗑️ Odebrat
                    </button>
                  </div>
                ))
              ) : null}
              <button
                onClick={() => {
                  const newFaq = [...(block.content || []), { question: '', answer: '' }]
                  updateBlockContent(block.id, newFaq)
                }}
                className="add-item-button"
              >
                ➕ Přidat otázku
              </button>
            </div>
          )}

          {block.type === 'wifi' && (
            <div>
              {Array.isArray(block.content) ? (
                block.content.map((item: any, index: number) => (
                  <div key={index} className="wifi-item">
                    <label>Název:</label>
                    <input
                      type="text"
                      value={item.name || ''}
                      onChange={(e) => {
                        const newWifi = [...block.content]
                        newWifi[index] = { ...newWifi[index], name: e.target.value }
                        updateBlockContent(block.id, newWifi)
                      }}
                      className="editor-input"
                    />
                    <label>SSID:</label>
                    <input
                      type="text"
                      value={item.ssid || ''}
                      onChange={(e) => {
                        const newWifi = [...block.content]
                        newWifi[index] = { ...newWifi[index], ssid: e.target.value }
                        updateBlockContent(block.id, newWifi)
                      }}
                      className="editor-input"
                    />
                    <label>Heslo:</label>
                    <input
                      type="text"
                      value={item.password || ''}
                      onChange={(e) => {
                        const newWifi = [...block.content]
                        newWifi[index] = { ...newWifi[index], password: e.target.value }
                        updateBlockContent(block.id, newWifi)
                      }}
                      className="editor-input"
                    />
                    <button
                      onClick={() => {
                        const newWifi = block.content.filter((_: any, i: number) => i !== index)
                        updateBlockContent(block.id, newWifi)
                      }}
                      className="remove-item-button"
                    >
                      🗑️ Odebrat
                    </button>
                  </div>
                ))
              ) : null}
              <button
                onClick={() => {
                  const newWifi = [...(block.content || []), { name: '', ssid: '', password: '' }]
                  updateBlockContent(block.id, newWifi)
                }}
                className="add-item-button"
              >
                ➕ Přidat Wi-Fi síť
              </button>
            </div>
          )}

          {block.type === 'contact' && (
            <div>
              <label>Popis:</label>
              <textarea
                value={block.content?.description || ''}
                onChange={(e) =>
                  updateBlockContent(block.id, {
                    ...block.content,
                    description: e.target.value
                  })
                }
                className="editor-textarea"
                rows={2}
              />
              <label>Email:</label>
              <input
                type="email"
                value={block.content?.email || ''}
                onChange={(e) =>
                  updateBlockContent(block.id, {
                    ...block.content,
                    email: e.target.value
                  })
                }
                className="editor-input"
              />
              <label>Telefon:</label>
              <input
                type="text"
                value={block.content?.phone || ''}
                onChange={(e) =>
                  updateBlockContent(block.id, {
                    ...block.content,
                    phone: e.target.value
                  })
                }
                className="editor-input"
              />
            </div>
          )}

          {/* Подблоки */}
          {block.subBlocks && block.subBlocks.length > 0 && (
            <div className="subblocks-container">
              <h4>Podbloky:</h4>
              {block.subBlocks.map((subBlock) => (
                <div key={subBlock.id} className="subblock-editor">
                  <div className="subblock-header">
                    <input
                      type="text"
                      value={subBlock.title || ''}
                      onChange={(e) =>
                        updateSubBlock(block.id, subBlock.id, 'title', e.target.value)
                      }
                      className="editor-input"
                      placeholder="Nadpis podbloku"
                    />
                    <button
                      onClick={() => deleteSubBlock(block.id, subBlock.id)}
                      className="remove-item-button"
                    >
                      🗑️
                    </button>
                  </div>
                  <textarea
                    value={typeof subBlock.content === 'string' ? subBlock.content : JSON.stringify(subBlock.content)}
                    onChange={(e) =>
                      updateSubBlock(block.id, subBlock.id, 'content', e.target.value)
                    }
                    className="editor-textarea"
                    rows={2}
                    placeholder="Obsah podbloku"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  if (loading) {
    return <div className="loading">Načítání...</div>
  }

  return (
    <div className="it-admin-info-editor">
      <div className="editor-header">
        <h2>Úprava informací IT administrátora</h2>
        <p style={{ color: '#666', marginBottom: '2rem' }}>
          Zde můžete upravit obsah stránky "Informace od IT administrátora". Můžete přidávat, mazat a přesouvat bloky.
        </p>
      </div>

      <div className="blocks-container">
        {content.blocks
          .sort((a, b) => a.order - b.order)
          .map(block => renderBlockEditor(block))}
      </div>

      <div className="add-block-section">
        <h3>Přidat nový blok:</h3>
        <div className="add-block-buttons">
          <button onClick={() => addBlock('text')} className="add-block-button">
            ➕ Text
          </button>
          <button onClick={() => addBlock('list')} className="add-block-button">
            ➕ Seznam
          </button>
          <button onClick={() => addBlock('link')} className="add-block-button">
            ➕ Odkaz
          </button>
          <button onClick={() => addBlock('faq')} className="add-block-button">
            ➕ FAQ
          </button>
          <button onClick={() => addBlock('wifi')} className="add-block-button">
            ➕ Wi-Fi
          </button>
          <button onClick={() => addBlock('contact')} className="add-block-button">
            ➕ Kontakt
          </button>
        </div>
      </div>

      <div className="editor-actions">
        <button
          onClick={handleSave}
          disabled={saving}
          className="save-button"
        >
          {saving ? 'Ukládání...' : '💾 Uložit změny'}
        </button>
        <button
          onClick={() => window.open('/it-admin-info', '_blank')}
          className="preview-button"
        >
          👁️ Zobrazit stránku
        </button>
      </div>
    </div>
  )
}
